import type { OnboardingV2Variant } from '@/app-shell/flags/growth-flags';
import { GET_ONBOARDING_V2_VARIANT } from '@/app-shell/flags/growth-flags';
import { LABS_FEATURE } from '@/app-shell/settings/heidi-labs/early-access/labs-feature-ids';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import type { UtmParams } from '@/utils/utmTracking';
import type { IntegrationType } from '@heidi/integrations/ehr/contracts';

export enum SignOutLocation {
  NAVIGATION_SIDEBAR = 'navigation_sidebar',
  WIDGET_AUTH_PAGE = 'widget_auth_page',
}

export type SignInMethod = 'email' | 'google' | 'apple';

export type ProviderValue = 'kinde' | 'auth0';
export type AuthType = 'email' | 'google' | 'apple' | 'sso';

export type SessionPageV2EnrollmentSource =
  | 'auto_enrolled'
  | 'labs_opted_in'
  | 'not_enrolled';

/**
 * Derives the persisted source of Session Page V2 enrollment for analytics.
 * This intentionally does not describe the currently rendered layout: the new
 * session-list and Session Page V2 release flags remain independent kill
 * switches. The backend-owned auto-enrollment stamp wins over any Labs
 * override, matching only the enrollment portion of the layout resolution.
 */
export function getSessionPageV2EnrollmentSource(
  user?: {
    scribe_session_page_v2_enabled?: boolean | null;
    session_settings?: {
      labs_feature_overrides?: Record<string, boolean> | null;
    } | null;
  } | null
): SessionPageV2EnrollmentSource {
  if (user?.scribe_session_page_v2_enabled === true) return 'auto_enrolled';
  if (
    user?.session_settings?.labs_feature_overrides?.[LABS_FEATURE.sessionLayoutV2] ===
    true
  ) {
    return 'labs_opted_in';
  }
  return 'not_enrolled';
}

export function mapAuthProviderToProvider(
  authProvider: AuthProvider | undefined
): ProviderValue | undefined {
  if (!authProvider) return undefined;
  if (authProvider === AuthProvider.KINDE) return 'kinde';
  return 'auth0';
}

export function mapSignInMethodToAuthType(
  signInMethod: string | undefined
): AuthType | undefined {
  if (!signInMethod) return undefined;
  const mapping: Record<string, AuthType> = {
    email: 'email',
    google: 'google',
    apple: 'apple',
    sso: 'sso',
  };
  return mapping[signInMethod];
}

export type UserSignedInEventProps = {
  integration_type?: IntegrationType;
  auth_provider?: AuthProvider;
  sign_in_method?: SignInMethod;
  provider?: ProviderValue;
  auth_type?: AuthType;
};

export type UserSignedUpEventProps = {
  integration_type?: IntegrationType;
  auth_provider?: AuthProvider;
  sign_in_method?: SignInMethod;
  provider?: ProviderValue;
  auth_type?: AuthType;
  custom_utm_source?: string | null;
  baa_required?: boolean;
  compliance_kit_required?: boolean;
  phone_number_provided?: boolean;
  /** A/B variant: `control` (v1) | `experimental` (v2) | `experimental-2` (v2 follow-up). */
  onboarding_variant?: OnboardingV2Variant;
};

/**
 * Props for the LOAD-BEARING `signup_conversion` marketing event.
 *
 * Carries the full client-side attribution set (`UtmParams`: all UTMs + every
 * ad-network click ID) plus the original landing URL — the signals only the
 * browser has and that Meta needs for conversion attribution. Business/account
 * enrichment is NOT duplicated here; the paired server-side `sign_up_successful`
 * is that authority, and Meta deduplicates the browser + server pair on its side.
 */
export type SignupConversionEventProps = UtmParams & {
  provider?: ProviderValue;
  /**
   * Deduplication key for Meta's browser↔server pairing: set to the new user's
   * `kinde_user_id`. The paired server-side `sign_up_successful` must emit the
   * same `event_id` for Meta to dedupe the two.
   */
  event_id?: string | null;
  /** Meta Pixel browser id (`_fbp` cookie) — for Meta Advanced Matching. */
  fbp?: string;
  /** Meta click id in `fb.1.{ms}.{fbclid}` format (`_fbc` cookie or synthesised). */
  fbc?: string;
  /** Full original landing URL (with attribution params), captured before the OAuth redirect strips them. */
  landing_url?: string | null;
  /** Convenience mirror of `utm_source` (parity with the legacy `signup_successful` event). */
  custom_utm_source?: string | null;
};

export type SignOutEventProps = {
  screen_section: SignOutLocation;
  auth_provider?: AuthProvider;
  provider?: ProviderValue;
};

// authentication_clicked event
export type AuthenticationClickedEventProps = {
  click_action?: string;
  provider?: ProviderValue;
  auth_type?: AuthType;
  /** A/B variant the user was bucketed into for the onboarding experiment. */
  onboarding_variant?: OnboardingV2Variant;
};

/**
 * Single owner of the authentication_clicked payload shape. Callers supply
 * the click action and their provider source (Redux store vs persisted
 * storage); auth_type derivation and onboarding-variant sourcing live here.
 */
export function buildAuthenticationClickedProps({
  clickAction,
  provider,
}: {
  clickAction?: string;
  provider?: ProviderValue;
}): AuthenticationClickedEventProps {
  return {
    click_action: clickAction,
    provider,
    auth_type: mapSignInMethodToAuthType(clickAction),
    onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
  };
}

// user_updated event
export type UserUpdatedAction = 'phone_number_changed';

export type UserUpdatedEventProps = {
  update_action: UserUpdatedAction;
  country?: string;
};

// user_started event
export type UserStartedEventProps = {
  source?: string;
};

// user_clicked event
export type UserClickedEventProps = {
  click_action?: string;
};

// account_deleted event
export type AccountDeletedEventProps = {
  reason?: string;
  details?: string;
};

// help_viewed event
export type HelpViewedEventProps = {
  article_url?: string;
};

// build omitted (not null) on local / non-publish builds
export type ApplicationInstalledEventProps = {
  version: string;
  build?: string;
  platform: string;
};

// auth manual control events (desktop auth0 flow)
export type AuthManualControlEventProps = {
  auth_provider: AuthProvider;
  attempt_id: string | null;
  browser_name: string;
  has_auth_url: boolean;
  copy_count: number;
  open_again_count: number;
  fallback_count: number;
  last_action: string | null;
  launched_at: number | null;
  component: string;
  action_source: string;
};
